import {
  DEFAULT_SOURCE_ATTACH,
  DEFAULT_TARGET_ATTACH,
  attachmentToPoint,
  type EdgeAttachment,
  type FlowPoint,
} from 'components/IncidentConfig/roster/rosterEdgeAttach';

/**
 * Connector routing for the org chart.
 *
 * The layout hands over a route per reporting line that is clear of boxes by
 * construction. Boxes then get measured, filtered, restyled and shifted apart
 * by the collision pass, and any of those can leave a connector drawn through a
 * seat it has nothing to do with. This module is the last word: it keeps a
 * route that is clear, and replaces one that is not with the nearest orthogonal
 * route that is, so a line never runs through a position or a resource.
 */

/** A seat box on the chart, in chart coordinates. */
export type ChartRect = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ConnectorInput = {
  id: string;
  source: string;
  target: string;
  /** Bend points from the layout, in chart coordinates. */
  waypoints?: FlowPoint[];
  sourceAttach?: EdgeAttachment;
  targetAttach?: EdgeAttachment;
};

export type RoutedConnector = {
  id: string;
  source: string;
  target: string;
  /** Orthogonal polyline from the source box to the target box. */
  points: FlowPoint[];
  /** True when the layout's route hit a box and was replaced. */
  rerouted: boolean;
};

/** Distance a line keeps from a box it is not attached to. */
export const CONNECTOR_CLEARANCE = 6;
/** Length of the straight run out of a box before the route may turn. */
const STUB = 10;
const EPSILON = 0.5;

type Segment = { from: FlowPoint; to: FlowPoint };

const nearlyEqual = (a: number, b: number) => Math.abs(a - b) < EPSILON;

/**
 * Whether an axis-aligned segment passes through the inside of a rect. A line
 * touching the outline is fine — that is what an attachment is.
 */
export function segmentCrossesRect(
  segment: Segment,
  rect: ChartRect,
  clearance = 0
): boolean {
  const left = rect.x - clearance;
  const right = rect.x + rect.width + clearance;
  const top = rect.y - clearance;
  const bottom = rect.y + rect.height + clearance;

  const minX = Math.min(segment.from.x, segment.to.x);
  const maxX = Math.max(segment.from.x, segment.to.x);
  const minY = Math.min(segment.from.y, segment.to.y);
  const maxY = Math.max(segment.from.y, segment.to.y);

  return (
    minX < right - EPSILON &&
    maxX > left + EPSILON &&
    minY < bottom - EPSILON &&
    maxY > top + EPSILON
  );
}

function segmentsOf(points: FlowPoint[]): Segment[] {
  const segments: Segment[] = [];
  for (let index = 1; index < points.length; index += 1) {
    segments.push({ from: points[index - 1], to: points[index] });
  }
  return segments;
}

/** Ids of the boxes a polyline runs through, excluding the ones it joins. */
export function boxesCrossedBy(
  points: FlowPoint[],
  obstacles: ChartRect[],
  clearance = CONNECTOR_CLEARANCE
): string[] {
  const hits: string[] = [];
  const segments = segmentsOf(points);
  for (const rect of obstacles) {
    if (
      segments.some((segment) => segmentCrossesRect(segment, rect, clearance))
    )
      hits.push(rect.id);
  }
  return hits;
}

/**
 * Turn any polyline into an orthogonal one: a diagonal jump becomes a vertical
 * run followed by a horizontal one, which is how the layout's elbows are meant
 * to read. Duplicate and collinear points are dropped.
 */
export function orthogonalize(points: FlowPoint[]): FlowPoint[] {
  const expanded: FlowPoint[] = [];
  for (const point of points) {
    const previous = expanded[expanded.length - 1];
    if (
      previous &&
      !nearlyEqual(previous.x, point.x) &&
      !nearlyEqual(previous.y, point.y)
    ) {
      expanded.push({ x: previous.x, y: point.y });
    }
    expanded.push(point);
  }
  return simplify(expanded);
}

function simplify(points: FlowPoint[]): FlowPoint[] {
  const result: FlowPoint[] = [];
  for (const point of points) {
    const last = result[result.length - 1];
    if (last && nearlyEqual(last.x, point.x) && nearlyEqual(last.y, point.y))
      continue;
    result.push(point);
  }
  // Drop the middle of three collinear points.
  for (let index = result.length - 2; index > 0; index -= 1) {
    const a = result[index - 1];
    const b = result[index];
    const c = result[index + 1];
    const verticalRun = nearlyEqual(a.x, b.x) && nearlyEqual(b.x, c.x);
    const horizontalRun = nearlyEqual(a.y, b.y) && nearlyEqual(b.y, c.y);
    if (verticalRun || horizontalRun) result.splice(index, 1);
  }
  return result;
}

/** SVG path for an orthogonal polyline. */
export function polylineToPath(points: FlowPoint[]): string {
  if (points.length === 0) return '';
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');
}

/** The point one stub length outside the box from an attachment. */
function stubOut(point: FlowPoint, attach: EdgeAttachment): FlowPoint {
  switch (attach.side) {
    case 'top':
      return { x: point.x, y: point.y - STUB };
    case 'bottom':
      return { x: point.x, y: point.y + STUB };
    case 'left':
      return { x: point.x - STUB, y: point.y };
    case 'right':
    default:
      return { x: point.x + STUB, y: point.y };
  }
}

function uniqueSorted(values: number[], around: number): number[] {
  const unique = [...new Set(values.map((value) => Math.round(value)))];
  return unique.sort((a, b) => Math.abs(a - around) - Math.abs(b - around));
}

/**
 * Candidate orthogonal routes between two free points, cheapest first: an L,
 * then a three-segment elbow through a horizontal or vertical channel, then a
 * five-segment detour around whatever sits between them.
 */
function candidateRoutes(
  from: FlowPoint,
  to: FlowPoint,
  obstacles: ChartRect[],
  leavesVertically: boolean
): FlowPoint[][] {
  const midY = (from.y + to.y) / 2;
  const midX = (from.x + to.x) / 2;
  const gap = CONNECTOR_CLEARANCE * 2;

  const bounds = obstacles.reduce(
    (acc, rect) => ({
      minX: Math.min(acc.minX, rect.x),
      maxX: Math.max(acc.maxX, rect.x + rect.width),
      minY: Math.min(acc.minY, rect.y),
      maxY: Math.max(acc.maxY, rect.y + rect.height),
    }),
    {
      minX: Math.min(from.x, to.x),
      maxX: Math.max(from.x, to.x),
      minY: Math.min(from.y, to.y),
      maxY: Math.max(from.y, to.y),
    }
  );

  const channelYs = uniqueSorted(
    [
      midY,
      ...obstacles.flatMap((rect) => [
        rect.y - gap,
        rect.y + rect.height + gap,
      ]),
      bounds.minY - gap,
      bounds.maxY + gap,
    ],
    midY
  );
  const channelXs = uniqueSorted(
    [
      midX,
      ...obstacles.flatMap((rect) => [rect.x - gap, rect.x + rect.width + gap]),
      bounds.minX - gap,
      bounds.maxX + gap,
    ],
    midX
  );

  // A line leaving the bottom of a box reads best dropping to a crossbar and
  // then across; one leaving a side reads best running across to a rail and
  // then along it. Try the family that matches the exit first.
  const throughY = channelYs.map((y) => [
    from,
    { x: from.x, y },
    { x: to.x, y },
    to,
  ]);
  const throughX = channelXs.map((x) => [
    from,
    { x, y: from.y },
    { x, y: to.y },
    to,
  ]);
  const elbows: FlowPoint[][] = leavesVertically
    ? [
        [from, { x: from.x, y: to.y }, to],
        [from, { x: to.x, y: from.y }, to],
      ]
    : [
        [from, { x: to.x, y: from.y }, to],
        [from, { x: from.x, y: to.y }, to],
      ];

  const routes: FlowPoint[][] = leavesVertically
    ? [...throughY, ...elbows, ...throughX]
    : [...throughX, ...elbows, ...throughY];

  // Detour: leave along the source's axis to the nearest clear channel, cross
  // over on a clear column, and come back along the target's axis.
  const forward = to.y >= from.y;
  const firstY = channelYs
    .filter((y) => (forward ? y > from.y : y < from.y))
    .sort((a, b) => Math.abs(a - from.y) - Math.abs(b - from.y))[0];
  const lastY = channelYs
    .filter((y) => (forward ? y < to.y : y > to.y))
    .sort((a, b) => Math.abs(a - to.y) - Math.abs(b - to.y))[0];
  if (firstY !== undefined && lastY !== undefined) {
    for (const x of channelXs) {
      routes.push([
        from,
        { x: from.x, y: firstY },
        { x, y: firstY },
        { x, y: lastY },
        { x: to.x, y: lastY },
        to,
      ]);
    }
  }

  return routes;
}

/**
 * Route every connector so that none of them runs through a box other than the
 * two it joins. Routes the layout supplied are kept whenever they are already
 * clear, so the chart looks the way the layout drew it and only the offending
 * lines move.
 */
export function routeConnectors(input: {
  rects: ChartRect[];
  connectors: ConnectorInput[];
}): RoutedConnector[] {
  const rectById = new Map(input.rects.map((rect) => [rect.id, rect]));

  return input.connectors.flatMap((connector) => {
    const sourceRect = rectById.get(connector.source);
    const targetRect = rectById.get(connector.target);
    if (!sourceRect || !targetRect) return [];

    const sourceAttach = connector.sourceAttach ?? DEFAULT_SOURCE_ATTACH;
    const targetAttach = connector.targetAttach ?? DEFAULT_TARGET_ATTACH;
    const sourcePoint = attachmentToPoint(
      sourceRect.x,
      sourceRect.y,
      sourceAttach,
      sourceRect
    );
    const targetPoint = attachmentToPoint(
      targetRect.x,
      targetRect.y,
      targetAttach,
      targetRect
    );

    const obstacles = input.rects.filter(
      (rect) => rect.id !== connector.source && rect.id !== connector.target
    );

    const proposed = orthogonalize([
      sourcePoint,
      ...(connector.waypoints ?? []),
      targetPoint,
    ]);
    if (
      connector.waypoints?.length &&
      boxesCrossedBy(proposed, obstacles).length === 0
    ) {
      return [{ ...identity(connector), points: proposed, rerouted: false }];
    }

    // Without a layout route the plain elbow is the first choice, and it is
    // only replaced if it hits something.
    const sourceStub = stubOut(sourcePoint, sourceAttach);
    const targetStub = stubOut(targetPoint, targetAttach);
    const leavesVertically =
      sourceAttach.side === 'top' || sourceAttach.side === 'bottom';
    const candidates = candidateRoutes(
      sourceStub,
      targetStub,
      obstacles,
      leavesVertically
    ).map((middle) => simplify([sourcePoint, ...middle, targetPoint]));

    let best: FlowPoint[] | null = null;
    let bestHits = Infinity;
    for (const candidate of candidates) {
      const hits = boxesCrossedBy(candidate, obstacles).length;
      if (hits === 0) {
        best = candidate;
        bestHits = 0;
        break;
      }
      if (hits < bestHits) {
        best = candidate;
        bestHits = hits;
      }
    }

    return [
      {
        ...identity(connector),
        points: best ?? proposed,
        // Only a layout route that had to be abandoned counts as rerouted; a
        // connector the layout never routed simply gets its first route here.
        rerouted: !!connector.waypoints?.length,
      },
    ];
  });
}

const identity = (connector: ConnectorInput) => ({
  id: connector.id,
  source: connector.source,
  target: connector.target,
});
